import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import { createHead } from "@unhead/vue";
import VueGtag from "vue-gtag";
import VueCookies from "vue-cookies";
import VueAxios from "vue-axios";
import axios from "axios";

import App from "./App.vue";
import router from "./router";
import store from "./store"; // Import Vuex store
import vuetify from "./plugins/vuetify"; // Import custom Vuetify configuration
import "./plugins"; // Import other plugins (excluding vue-meta)

// Vuetify styles
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";

// Global custom styles
import "@/styles/global.scss";

// Localization
import { languages } from "./locales/index";
import { defaultLocale } from "./locales/index";
const messages = Object.assign(languages);

// Configure axios defaults
axios.defaults.withCredentials = true;

let csrfFetchPromise: Promise<void> | null = null;

function isUsableCsrfToken(token: unknown): token is string {
  return typeof token === "string" && token.trim().length > 0;
}

async function ensureCsrfToken(): Promise<string> {
  if (isUsableCsrfToken(store.state.csrfToken)) {
    return store.state.csrfToken;
  }

  if (!csrfFetchPromise) {
    csrfFetchPromise = store
      .dispatch("FETCH_CSRF_TOKEN")
      .finally(() => {
        csrfFetchPromise = null;
      });
  }

  await csrfFetchPromise;

  const token = store.state.csrfToken;
  if (!isUsableCsrfToken(token)) {
    throw new Error("CSRF token missing");
  }

  return token;
}

async function forceRefreshCsrfToken(): Promise<string> {
  store.commit("SET_CSRF_TOKEN", null);

  if (!csrfFetchPromise) {
    csrfFetchPromise = store
      .dispatch("FETCH_CSRF_TOKEN")
      .finally(() => {
        csrfFetchPromise = null;
      });
  }

  await csrfFetchPromise;

  const token = store.state.csrfToken;
  if (!isUsableCsrfToken(token)) {
    throw new Error("CSRF token missing");
  }

  return token;
}

function isCsrfForbiddenError(error: any): boolean {
  const status = error?.response?.status;
  if (status !== 403) {
    return false;
  }

  const message = error?.response?.data?.message;
  return typeof message === "string" && message.toLowerCase().includes("csrf");
}

// Add axios interceptors for CSRF token
axios.interceptors.request.use(async (config) => {
  const isStateChanging = ["post", "put", "delete", "patch"].includes(
    config.method?.toLowerCase() || ""
  );

  if (isStateChanging) {
    const token = await ensureCsrfToken();
    if (!config.headers) {
      config.headers = {} as any;
    }
    config.headers["x-csrf-token"] = token;
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle CSRF token refresh on 403 errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isCsrfForbiddenError(error) && error.config && !error.config._retry) {
      error.config._retry = true;
      
      try {
        const token = await forceRefreshCsrfToken();
        if (!error.config.headers) {
          error.config.headers = {} as any;
        }
        error.config.headers["x-csrf-token"] = token;
        return axios(error.config);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Initialize app in async function
(async () => {

  const app = createApp(App);

  app.use(store);

  app.use(router);

  app.use(vuetify);

  const i18n = createI18n({
    legacy: false,
    locale: defaultLocale,
    fallbackLocale: "en",
    messages
  });
  app.use(i18n);

  const head = createHead();
  app.use(head);

  app.use(VueCookies);
  app.use(VueAxios, axios);

  app.use(
    VueGtag,
    {
      config: {
        id: import.meta.env.VITE_APP_ANALYTICS_ID
      }
    },
    router
  );

  // Mount the app
  app.mount("#app");
})();
